import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Car, ArrowRight, WhatsappLogo, Phone, EnvelopeSimple, Briefcase } from "@phosphor-icons/react";
import { SDRIVE_CONTACT, openWhatsApp, toWhatsAppNumber } from "@/lib/contact";
import { useI18n } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import PublicFooter from "@/components/PublicFooter";

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useI18n();

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-zinc-800/50 bg-[#050505]/90 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2 sm:gap-3">
          <button
            data-testid="landing-logo-home"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 sm:gap-3 min-w-0 focus:outline-none focus:ring-2 focus:ring-amber-500/40 rounded"
            aria-label="Sdrive Transfert — Accueil"
          >
            <img src="/brand/sdrive-logo.png" alt="Sdrive Transfert" className="h-9 w-auto" />
            <h1 className="text-base sm:text-lg font-medium truncate">Sdrive Transfert</h1>
          </button>
          <div className="flex-1" />
          <LanguageSwitcher testid="landing-lang" />
          <ThemeToggle testid="landing-theme" />
          <Button
            data-testid="landing-login"
            onClick={() => navigate("/login")}
            className="bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700 font-semibold h-9 px-3 sm:px-4 shrink-0"
          >
            <Briefcase size={16} weight="fill" className="mr-1.5 text-amber-500" />
            <span className="hidden sm:inline">Se connecter</span>
            <span className="sm:hidden">Connexion</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-10">
        <div className="text-center">
          <p className="text-amber-400 text-xs sm:text-sm uppercase tracking-widest mb-3">{t("landing.eyebrow")}</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mb-5">
            {t("landing.title_1")} <span className="text-amber-400">{t("landing.title_accent")}</span><br className="hidden sm:inline" /> {t("landing.title_2")}
          </h2>
          <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto mb-8">
            {t("landing.intro")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              data-testid="cta-book"
              onClick={() => navigate("/request")}
              className="bg-amber-500 text-black font-semibold hover:bg-amber-600 h-12 px-6 text-base"
            >
              <Car size={20} className="mr-2" weight="fill" /> {t("landing.cta_book")}
              <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button
              data-testid="cta-whatsapp"
              variant="outline"
              onClick={() => openWhatsApp(SDRIVE_CONTACT.phoneRaw, "Bonjour, je souhaite réserver une course VTC.")}
              className="border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 h-12 px-6 text-base"
            >
              <WhatsappLogo size={20} className="mr-2" weight="fill" /> {t("landing.cta_wa")}
            </Button>
          </div>
          {/* Phase 2D — Accès discret pour clients existants */}
          <p className="text-xs text-zinc-500 mt-5">
            Déjà client ?{" "}
            <button
              data-testid="landing-existing-login"
              onClick={() => navigate("/login")}
              className="text-amber-500 hover:text-amber-400 underline-offset-2 hover:underline"
            >
              Se connecter
            </button>
            {" · "}
            <button
              data-testid="landing-quote-recovery"
              onClick={() => navigate("/quote-recovery")}
              className="text-amber-500 hover:text-amber-400 underline-offset-2 hover:underline"
            >
              Retrouver une commande
            </button>
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-16">
          <Feature title={t("landing.feat_noacc_t")} desc={t("landing.feat_noacc_d")} />
          <Feature title={t("landing.feat_price_t")} desc={t("landing.feat_price_d")} />
          <Feature title={t("landing.feat_drv_t")} desc={t("landing.feat_drv_d")} />
        </div>

        <div className="mt-16 bg-[#121212] border border-zinc-800 rounded-xl p-6 text-center">
          <p className="text-sm text-zinc-500 uppercase tracking-wider mb-3">{t("landing.contact")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a data-testid="contact-call" href={`tel:${SDRIVE_CONTACT.phoneRaw}`} className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 text-sm font-medium">
              <Phone size={16} weight="fill" /> {SDRIVE_CONTACT.phone}
            </a>
            <a data-testid="contact-wa" href={`https://wa.me/${toWhatsAppNumber(SDRIVE_CONTACT.phoneRaw)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/20 text-sm font-medium">
              <WhatsappLogo size={16} weight="fill" /> WhatsApp
            </a>
            <a data-testid="contact-email" href={`mailto:${SDRIVE_CONTACT.email}`} className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-sky-500/10 text-sky-400 border border-sky-500/30 hover:bg-sky-500/20 text-sm font-medium">
              <EnvelopeSimple size={16} weight="fill" /> {SDRIVE_CONTACT.email}
            </a>
          </div>
        </div>
      </main>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-center text-[11px] text-zinc-600">
        © Sdrive Transfert — {t("landing.footer")} · <button onClick={() => navigate("/request")} className="underline hover:text-zinc-400">{t("landing.req_link")}</button> · <button onClick={() => navigate("/login")} className="underline hover:text-zinc-400">{t("landing.pro_area")}</button>
      </footer>
      <PublicFooter className="border-t-zinc-900/40" />
    </div>
  );
}

function Feature({ title, desc }) {
  return (
    <div className="bg-[#121212] border border-zinc-800 rounded-lg p-5">
      <h3 className="text-white font-medium mb-1.5">{title}</h3>
      <p className="text-sm text-zinc-400">{desc}</p>
    </div>
  );
}
